/**
 * The Position Tracker bot view: TWO discrete sections — "My Bots" (claimed by
 * you) and "Org bots" (the pool you haven't) — that a bot moves between when you
 * claim or release it. Each section is the SAME rich race-board as the Bots page
 * (<FleetBoard>: fill anchor, heat ramp, full metric band, climb sparkline, claim
 * cell, controls, queue-ladder detail) and carries its OWN view mode (compact /
 * card / table) + open-row, so the two are managed independently. My = isMine,
 * Org = !isMine — exact complements over the fleet the relay returns. That fleet
 * IS org-scoped server-side now: a manager/trader gets only their own org's bots,
 * and an admin gets the org they've PICKED (selectedOrgId → ?orgId) plus any
 * unscoped/legacy bot. So this client split partitions an already-org-bounded set.
 *
 * Self-gating + self-contained — mount it once in the tracker. Renders nothing
 * unless the viewer can see bots (a bot-access trader, or a manager/admin), the
 * edge is configured, and at least one bot is running. Controls (claim/release,
 * pause/resume, stop) come from useEdgeFleet; mutating controls are gated by the
 * relay's server-computed canOperate (org membership; admin role does NOT bypass)
 * so none silently 403; reband is omitted (no edge endpoint here), so its
 * affordance hides. Stop is confirmed (one shared modal).
 */
import { useMemo, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useDisplaySettings } from '@/stores/displaySettings';
import { useOrgScope } from '@/stores/orgScope';
import { useEdgeFleet } from '@/hooks/useEdgeFleet';
import { SummaryStrip, SummaryTile, ViewModeToggle } from '@/components/cards/cardKit';
import { ConfirmModal } from '@/components/atoms/Modal';
import { OctagonX } from 'lucide-react';
import { FleetBoard, type FleetControls } from './FleetBoard';
import { killAllBots, deleteAgent, type AgentStats } from '@/lib/bots/edge';
import {
  FRONT_AT,
  frontOf,
  restingTotal,
  type Motion,
  type SortKey,
  type ViewMode,
} from '@/lib/bots/telemetry';

const BOT_SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'front', label: 'closest to front' },
  { value: 'eta', label: 'soonest fill (est)' },
  { value: 'net', label: 'most inventory drift' },
  { value: 'errors', label: 'most errors' },
];

function sortBots(agents: AgentStats[], motion: Map<string, Motion>, sort: SortKey): AgentStats[] {
  const arr = [...agents];
  const fr = (a: AgentStats) => {
    const f = frontOf(a);
    return f < 0 ? Infinity : f;
  };
  if (sort === 'front') arr.sort((a, b) => fr(a) - fr(b));
  else if (sort === 'eta') {
    arr.sort((a, b) => {
      const ea = motion.get(a.id)?.eta ?? -1;
      const eb = motion.get(b.id)?.eta ?? -1;
      return (ea < 0 ? Infinity : ea) - (eb < 0 ? Infinity : eb);
    });
  } else if (sort === 'net') {
    arr.sort((a, b) => Math.abs(b.netInventory) - Math.abs(a.netInventory));
  } else if (sort === 'errors') {
    arr.sort((a, b) => b.errors - a.errors);
  }
  return arr;
}

export function BotZones() {
  const me = useAuthStore((s) => s.user?.username);
  const canView = useAuthStore((s) => {
    const u = s.user;
    if (!u) return false;
    return (u.botAccess ?? false) || u.role === 'manager' || u.role === 'admin';
  });
  const isPriv = useAuthStore((s) => s.user?.role === 'manager' || s.user?.role === 'admin');
  // An admin can VIEW another org's fleet by picking it (the relay honors ?orgId
  // for admins only; interaction stays membership-gated server-side).
  const selectedOrgId = useOrgScope((s) => s.selectedOrgId);
  const fleet = useEdgeFleet({ enabled: canView, orgId: selectedOrgId });
  const [killId, setKillId] = useState<string | null>(null);
  // Kill-all per section: a snapshot of the operable ids + a label, taken at
  // click so the confirm count/target stay stable through the ~2s poll.
  const [killAllReq, setKillAllReq] = useState<{ label: string; ids: string[] } | null>(null);
  const [killAllBusy, setKillAllBusy] = useState(false);
  const [killAllNote, setKillAllNote] = useState<string | null>(null);
  const myView = useDisplaySettings((s) => s.myBotsViewMode);
  const setMyView = useDisplaySettings((s) => s.setMyBotsViewMode);
  const orgView = useDisplaySettings((s) => s.orgBotsViewMode);
  const setOrgView = useDisplaySettings((s) => s.setOrgBotsViewMode);
  const mySort = useDisplaySettings((s) => s.myBotsSort);
  const setMySort = useDisplaySettings((s) => s.setMyBotsSort);
  const orgSort = useDisplaySettings((s) => s.orgBotsSort);
  const setOrgSort = useDisplaySettings((s) => s.setOrgBotsSort);

  // No bot access → render nothing. But for someone who CAN see bots, an empty or
  // unconfigured lane should say WHY — otherwise "empty" reads as "no access."
  if (!canView) return null;
  if (fleet.status === 'loading') return null; // transient — don't flash a note
  if (fleet.status !== 'ok' || fleet.agents.length === 0) {
    const note =
      fleet.status === 'not-configured'
        ? 'Bot engine not configured on this lane.'
        : fleet.status === 'error'
          ? 'Bot feed unavailable right now.'
          : 'No bots running yet.';
    return (
      <div className="space-y-2">
        <h2 className="text-foreground text-sm font-semibold">Bots</h2>
        <div className="text-muted-foreground border-border rounded-md border border-dashed px-3 py-4 text-center text-xs">
          {note}
          {fleet.status === 'ok' && <span className="opacity-70"> Deploy one from the Value Dashboard.</span>}
        </div>
      </div>
    );
  }

  const isMine = (username?: string | null) => !!username && username === me;
  const mine = fleet.agents.filter((a) => isMine(a.owner?.username));
  const pool = fleet.agents.filter((a) => !isMine(a.owner?.username));

  // A trader may mutate only its own bots (the relay enforces owner-only authz);
  // managers/admins may mutate any. Gate pause/stop to match — a viewer never
  // sees a control that would just silently 403. Claim stays open to all.
  // Operate (pause/stop/reband) is gated by the relay's server-computed
  // canOperate (org-membership; admin role does NOT bypass — see edge-proxy).
  // Fallback to the old role/owner heuristic only if an older relay omitted it.
  const canMutate = (a: AgentStats) => a.canOperate ?? (isPriv || isMine(a.owner?.username));

  const controls: FleetControls = {
    pause: (id) => void fleet.pause(id),
    resume: (id) => void fleet.resume(id),
    kill: (id) => setKillId(id),
    claim: (id, ticker) => void fleet.claim(id, ticker),
    release: (id) => void fleet.release(id),
    // reband omitted — useEdgeFleet has no reband endpoint; the affordance hides.
  };

  // Per-section operable ids (only bots this viewer can actually stop → no
  // guaranteed-403). A trader has no operable bots in the Org pool, so its
  // Stop-all simply won't render for them.
  const mineOperable = mine.filter(canMutate).map((a) => a.id);
  const poolOperable = pool.filter(canMutate).map((a) => a.id);

  // Stop every operable bot in the snapshot via the SAME per-bot endpoint (a
  // client loop, not a new surface), refresh once, report honestly by count.
  async function runKillAll(label: string, ids: string[]) {
    setKillAllReq(null);
    setKillAllBusy(true);
    setKillAllNote(null);
    try {
      const r = await killAllBots(ids, deleteAgent);
      fleet.refresh();
      setKillAllNote(
        r.failed > 0
          ? `Stopped ${r.succeeded}/${r.attempted} ${label}; ${r.failed} did not confirm and may still be resting — check the fleet.`
          : `Stopped ${r.succeeded} ${label} bot${r.succeeded === 1 ? '' : 's'}.`,
      );
    } finally {
      setKillAllBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      {killAllNote && (
        <div className="flex items-start justify-between gap-3 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          <span>{killAllNote}</span>
          <button onClick={() => setKillAllNote(null)} className="shrink-0 text-muted-foreground/70 hover:text-foreground">
            dismiss
          </button>
        </div>
      )}
      <BotSection
        title="My Bots"
        help="Bots you've claimed. Their orders work in the background; once a bot takes a fill it becomes a position you own."
        agents={mine}
        motion={fleet.motion}
        view={myView}
        onViewChange={setMyView}
        sort={mySort}
        onSortChange={setMySort}
        meUsername={me}
        controls={controls}
        canMutate={canMutate}
        stopAllCount={mineOperable.length}
        stopAllBusy={killAllBusy}
        onStopAll={() => setKillAllReq({ label: 'My Bots', ids: mineOperable })}
        emptyHint="No bots claimed by you. Claim one from the org pool below, or deploy from the Value Dashboard."
      />
      <BotSection
        title="Org bots"
        help="Bots running for your org that you haven't claimed (unclaimed, or held by a teammate). Claim one to take the wheel."
        agents={pool}
        motion={fleet.motion}
        view={orgView}
        onViewChange={setOrgView}
        sort={orgSort}
        onSortChange={setOrgSort}
        meUsername={me}
        controls={controls}
        canMutate={canMutate}
        stopAllCount={poolOperable.length}
        stopAllBusy={killAllBusy}
        onStopAll={() => setKillAllReq({ label: 'Org bots', ids: poolOperable })}
        emptyHint="No bots in the org pool — every running bot is claimed by you."
      />

      <ConfirmModal
        isOpen={!!killId}
        onClose={() => setKillId(null)}
        onConfirm={() => {
          if (killId) void fleet.kill(killId);
          setKillId(null);
        }}
        title="Stop bot?"
        message="This cancels all of the bot's resting orders and removes it from the fleet."
        confirmLabel="Stop"
        variant="danger"
      />
      <ConfirmModal
        isOpen={!!killAllReq}
        onClose={() => setKillAllReq(null)}
        onConfirm={() => killAllReq && void runKillAll(killAllReq.label, killAllReq.ids)}
        title={`Stop all ${killAllReq?.label ?? ''}?`}
        message={`This attempts to stop ${killAllReq?.ids.length ?? 0} bot${killAllReq?.ids.length === 1 ? '' : 's'} you can operate — each cancels its resting orders best-effort. Verify on the venue afterward; any that don't confirm will be listed.`}
        confirmLabel="Stop all"
        variant="danger"
      />
    </div>
  );
}

/**
 * One discrete bot section (My Bots / Org bots) with its OWN view mode + open-row
 * state, so the two are managed independently. The header sits in the page
 * background (matches the Positions / Open Orders headers); the rich board is the
 * card beneath.
 */
function BotSection({
  title,
  help,
  agents,
  motion,
  view,
  onViewChange,
  sort,
  onSortChange,
  meUsername,
  controls,
  canMutate,
  stopAllCount,
  stopAllBusy,
  onStopAll,
  emptyHint,
}: {
  title: string;
  help: string;
  agents: AgentStats[];
  motion: Map<string, Motion>;
  view: ViewMode;
  onViewChange: (view: ViewMode) => void;
  sort: SortKey;
  onSortChange: (sort: SortKey) => void;
  meUsername?: string;
  controls: FleetControls;
  canMutate: (a: AgentStats) => boolean;
  /** number of bots in THIS section the viewer can stop; 0 hides Stop all. */
  stopAllCount: number;
  stopAllBusy: boolean;
  onStopAll: () => void;
  emptyHint: string;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const sortedAgents = useMemo(
    () => sortBots(agents, motion, sort),
    [agents, motion, sort],
  );
  const live = agents.filter((a) => !a.paused).length;
  const totalResting = agents.reduce((s, a) => s + restingTotal(a), 0);
  const netInv = agents.reduce((s, a) => s + a.netInventory, 0);
  const atFront = agents.filter((a) => {
    const front = frontOf(a);
    return front >= 0 && front <= FRONT_AT;
  }).length;
  const totalErrors = agents.reduce((s, a) => s + a.errors, 0);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-foreground flex items-center gap-2 text-sm font-semibold">
          {title}
          <span className="text-muted-foreground text-xs font-normal">{agents.length}</span>
        </h2>
        <div className="flex items-center gap-2">
          <label className="text-muted-foreground text-[11px]">sort</label>
          <select
            value={sort}
            onChange={(e) => onSortChange(e.target.value as SortKey)}
            className="bg-background border-border rounded border px-1.5 py-1 text-xs"
          >
            {BOT_SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <ViewModeToggle value={view} onChange={onViewChange} modes={['compact', 'card', 'table'] as const} />
          {stopAllCount > 0 && (
            <button
              onClick={onStopAll}
              disabled={stopAllBusy}
              title={`stop all ${stopAllCount} bot${stopAllCount === 1 ? '' : 's'} you can operate in ${title}`}
              className="inline-flex items-center gap-1 rounded border border-red-500/40 px-2 py-1 text-[11px] font-medium text-red-400 hover:bg-red-500/10 disabled:opacity-50"
            >
              <OctagonX size={12} /> {stopAllBusy ? 'Stopping…' : `Stop all (${stopAllCount})`}
            </button>
          )}
        </div>
      </div>
      {help && <p className="text-muted-foreground text-[11px]">{help}</p>}
      <SummaryStrip>
        <SummaryTile label="agents live" value={live} />
        <SummaryTile label="resting contracts" value={totalResting.toLocaleString()} />
        <SummaryTile
          label="net inventory"
          value={`${netInv > 0 ? '+' : ''}${netInv}`}
          tone={netInv !== 0 ? 'warning' : 'default'}
        />
        <SummaryTile label="at the front" value={atFront} tone={atFront > 0 ? 'danger' : 'default'} />
        <SummaryTile label="errors" value={totalErrors} tone={totalErrors > 0 ? 'danger' : 'default'} />
      </SummaryStrip>
      <FleetBoard
        agents={sortedAgents}
        motion={motion}
        view={view}
        openId={openId}
        onOpen={(id) => setOpenId((x) => (x === id ? null : id))}
        meUsername={meUsername}
        controls={controls}
        canMutate={canMutate}
        emptyHint={emptyHint}
      />
    </div>
  );
}
